import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {ExtendedWarrantyService} from '../../services/extended-warranty.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Factor} from '../../model/factor';
import {Entity} from '../../entity/entity';
import {ExtendedWarrantyUtils} from '../../utils/extended-warranty-utils';
import { HttpErrorResponse } from '@angular/common/http';
import {StringUtils} from '../../utils/string-utils';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';

@Component({
    selector: 'app-extended-warranty-dialog',
    templateUrl: './extended-warranty-dialog.component.html',
    styleUrls: ['./extended-warranty-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, CurrencyPipe]
})
export class ExtendedWarrantyDialogComponent implements OnInit {
  private readonly extendedWarrantyService = inject(ExtendedWarrantyService);
  private dialogRef = inject<MatDialogRef<ExtendedWarrantyDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);


  readonly extendedWarrantyCode = signal<string>('');
  readonly availableWarrantyCodes = signal<Factor[]>(undefined!);
  private readonly entity: Entity;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  warningMessage!: string;
  readonly estimatedPrice = signal<number>(undefined!);

  constructor() {
    const data = this.data;

    this.entity = JSON.parse(JSON.stringify(data.entity));
    if (!ExtendedWarrantyUtils.canWarrantyBeChanged(this.entity)) {
      this.warningMessage = 'You cannot change the extended warranty code for this entity';
    }
  }

  ngOnInit(): void {
    this.isBusy.set(true);
    this.extendedWarrantyService.getExtendedWarrantyFactors().subscribe({
      next: results => {
        this.availableWarrantyCodes.set(results.sort((first, second) => first.partsWarrantyMonths - second.partsWarrantyMonths));
        this.extendedWarrantyCode.set(this.entity.extendedWarrantyCode);
        this.updateSelection();
        this.isBusy.set(false);
      },
      error: error => this.handleError(error),
    });
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    this.isBusy.set(true);
    this.entity.extendedWarrantyCode = this.extendedWarrantyCode();
    this.extendedWarrantyService.addExtendedWarranty(this.entity).subscribe({
      next: () => {
        this.dialogRef.close(true);
        this.isBusy.set(false);
      },
      error: error => this.handleError(error),
    });
  }

  changesDisabled(): boolean {
    return !!(this.isBusy() || (this.warningMessage && this.warningMessage.length > 0));
  }

  updateSelection() {
    this.estimatedPrice.set(0);
    if (this.extendedWarrantyCode()) {
      const warrantyFactor = this.availableWarrantyCodes().filter(factor => factor.code === this.extendedWarrantyCode())[0].factorValue;
      this.estimatedPrice.set(this.getConfiguredNetPrice() * warrantyFactor);
    }
  }

  private getConfiguredNetPrice(): number {
    if (!this.entity || !this.entity.lines || this.entity.lines.length === 0) {
      return 0;
    }
    return this.entity.lines.filter(line => line.configured).map(line => line.finalExtendedPrice)
      .reduce((accumulator, price) => accumulator + price);
  }

}
